/** Vendor-wide provider variables (Connect step) — mirrored in adapter `c` at runtime. */

export const PROVIDER_VARIABLE_KEY_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
export const MAX_PROVIDER_VARIABLES = 20;

export const RESERVED_PROVIDER_VARIABLE_KEYS = new Set(['name', 'adminVendorId', 'model']);

export interface ProviderVariableRow {
  rowId: string;
  key: string;
  value: string;
}

export function providerVariablesFromRows(rows: ProviderVariableRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const key = row.key.trim();
    const value = row.value.trim();
    if (!key || !value) continue;
    out[key] = value;
  }
  return out;
}

export function providerVariableRowsFromRecord(
  vars: Record<string, string> | undefined,
): ProviderVariableRow[] {
  if (!vars) return [];
  return Object.entries(vars).map(([key, value]) => ({
    rowId: `pv-${key}`,
    key,
    value,
  }));
}

export function validateProviderVariableKey(key: string): string | null {
  const trimmed = key.trim();
  if (!trimmed) return 'Key is required';
  if (RESERVED_PROVIDER_VARIABLE_KEYS.has(trimmed)) {
    return 'Key is reserved (name, adminVendorId, and model are set automatically)';
  }
  if (!PROVIDER_VARIABLE_KEY_PATTERN.test(trimmed)) {
    return 'Key must start with a letter or underscore and contain only letters, numbers, and underscores';
  }
  return null;
}

export function hasPathTemplate(value: string): boolean {
  return /\{\w+\}/.test(value);
}

/** Replace `{{key}}` and `{key}` placeholders using provider variables. */
export function substituteProviderVariables(
  template: string,
  variables: Record<string, string>,
): string {
  let out = template;
  for (const [key, value] of Object.entries(variables)) {
    if (!key.trim() || value === undefined) continue;
    const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out.replace(new RegExp(`\\{\\{${escaped}\\}\\}`, 'g'), value);
    out = out.replace(new RegExp(`\\{${escaped}\\}`, 'g'), value);
  }
  return out;
}

/** JSONata `c` context for onboarding preview (matches adapter whitelist). */
export function buildPreviewProviderContext(
  vendorId: string,
  providerVariables: Record<string, string>,
): { name: string; adminVendorId: string; model?: string; [key: string]: string | undefined } {
  const model = providerVariables.model?.trim();
  const extra: Record<string, string> = {};
  let count = 0;
  for (const [key, value] of Object.entries(providerVariables)) {
    if (count >= MAX_PROVIDER_VARIABLES) break;
    if (RESERVED_PROVIDER_VARIABLE_KEYS.has(key) || !value.trim()) continue;
    if (!PROVIDER_VARIABLE_KEY_PATTERN.test(key)) continue;
    extra[key] = value.trim();
    count += 1;
  }
  return {
    name: vendorId.trim() || 'preview-vendor',
    adminVendorId: vendorId.trim() || 'preview-vendor',
    ...(model ? { model } : {}),
    ...extra,
  };
}

/** Flatten nested response JSONata preset object to dotted canonical field → expression map. */
export function flattenResponsePresetExpressions(presetJson: string): Record<string, string> {
  let root: unknown;
  try {
    root = JSON.parse(presetJson) as unknown;
  } catch {
    return {};
  }
  if (!root || typeof root !== 'object' || Array.isArray(root)) return {};
  const out: Record<string, string> = {};
  const walk = (prefix: string, val: unknown): void => {
    if (!val || typeof val !== 'object' || Array.isArray(val)) return;
    for (const [key, child] of Object.entries(val as Record<string, unknown>)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (child !== null && typeof child === 'object' && !Array.isArray(child)) {
        walk(path, child);
      } else if (typeof child === 'string' && child.trim()) {
        out[path] = child.trim();
      }
    }
  };
  walk('', root);
  return out;
}

export function formatJsonataError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === 'object') {
    const o = err as Record<string, unknown>;
    const parts: string[] = [];
    if (typeof o.message === 'string' && o.message) parts.push(o.message);
    if (typeof o.code === 'string' && o.code) parts.push(`code: ${o.code}`);
    if (typeof o.position === 'number') parts.push(`position: ${o.position}`);
    if (typeof o.token === 'string' && o.token) parts.push(`token: ${o.token}`);
    if (parts.length > 0) return parts.join(' · ');
  }
  if (typeof err === 'string' && err) return err;
  try {
    return JSON.stringify(err);
  } catch {
    return 'JSONata evaluation failed';
  }
}
